import { Activity, AlertTriangle, TrendingUp, BarChart3, PieChart as PieChartIcon, CheckCircle } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import type { GargaloItem, PieStatusItem, EvolucaoItem } from './master.types';
import { EmptyStateCard } from './EmptyStateCard';

interface OnboardingChartsRowProps {
  evolucaoData: EvolucaoItem[];
  pieStatus: PieStatusItem[];
  gargalosData: GargaloItem[];
  setupEmAndamento: number;
  setupConcluido?: number;
  totalEscolas?: number;
}

export function OnboardingChartsRow({
  evolucaoData,
  pieStatus,
  gargalosData,
  setupEmAndamento,
  setupConcluido = 0,
  totalEscolas = 0,
}: OnboardingChartsRowProps) {
  const pieAtivos = pieStatus.filter(p => p.value > 0);
  const pieRender = pieAtivos.length > 0 ? pieAtivos : [{ name: 'Sem dados', value: 1, color: '#e5e7eb' }];

  const totalMonitoradas = totalEscolas > 0 ? totalEscolas : (setupEmAndamento + setupConcluido);
  const pctConcluido = totalMonitoradas > 0 ? Math.round((setupConcluido / totalMonitoradas) * 100) : 0;
  const temDadosEvolucao = evolucaoData.some(d => d.emConfiguracao > 0 || d.concluidas > 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
      {/* Growth Chart */}
      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 16, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={18} style={{ color: '#10b981' }} />
            Ritmo de Onboarding (Últimos 15 dias)
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '.72rem', fontWeight: 700, color: '#2563eb', background: '#3b82f615', padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
              {setupEmAndamento} em configuração
            </span>
            <span style={{ fontSize: '.72rem', fontWeight: 700, color: '#059669', background: '#10b98115', padding: '3px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              {setupConcluido} concluída{setupConcluido !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: '.72rem', fontWeight: 600, color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: 6 }}>
              {pctConcluido}% concluído
            </span>
          </div>
        </div>
        <p style={{ fontSize: '.72rem', color: 'var(--text-muted, #9ca3af)', marginBottom: 12 }}>
          Evolução diária de escolas em configuração vs. setups concluídos (dados reais da rede)
        </p>
        {!temDadosEvolucao && totalMonitoradas === 0 ? (
          <EmptyStateCard
            icon={BarChart3}
            title="Aguardando dados de onboarding"
            description="Quando escolas começarem a ser configuradas, o ritmo de evolução aparecerá aqui."
            compact
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={evolucaoData}>
              <defs>
                <linearGradient id="emConfGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="concluidasGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <YAxis allowDecimals={false} domain={[0, 'auto']} tick={{ fontSize: 11, fill: '#6b7280' }} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const emConf = payload.find(p => p.dataKey === 'emConfiguracao')?.value ?? 0;
                  const conc = payload.find(p => p.dataKey === 'concluidas')?.value ?? 0;
                  return (
                    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '.8rem' }}>
                      <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Dia {label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6' }} />
                        Em Configuração: {emConf} escola{emConf !== 1 ? 's' : ''}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#059669', fontWeight: 600, marginTop: 2 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                        Concluídas: {conc} escola{conc !== 1 ? 's' : ''}
                      </div>
                    </div>
                  );
                }}
              />
              <Area type="monotone" name="Em Configuração" dataKey="emConfiguracao" stroke="#3b82f6" strokeWidth={3} fill="url(#emConfGrad)" />
              <Area type="monotone" name="Concluídas" dataKey="concluidas" stroke="#10b981" strokeWidth={3} fill="url(#concluidasGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Distribution + Gargalos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Pie Status */}
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 14, flex: 1 }}>
          <h3 style={{ fontSize: '.92rem', fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={16} style={{ color: '#8b5cf6' }} />
            Status (Fila Atual)
          </h3>
          {pieAtivos.length === 0 ? (
            <EmptyStateCard
              icon={PieChartIcon}
              title="Nenhuma escola na fila"
              description="Quando escolas estiverem em configuração, o status aparecerá aqui."
              compact
            />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={96}>
                <PieChart>
                  <Pie data={pieRender} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" paddingAngle={3}>
                    {pieRender.map(entry => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '.8rem' }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px 16px', marginTop: 4 }}>
                {pieAtivos.map(p => (
                  <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.75rem' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
                    <span>{p.name} ({p.value})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Gargalos */}
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 14, flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: '.92rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <AlertTriangle size={16} style={{ color: '#ef4444' }} />
              Onde as escolas estão travando
            </h3>
            <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 600 }}>escolas em andamento</span>
          </div>
          {gargalosData.length === 0 ? (
            <EmptyStateCard
              icon={CheckCircle}
              title="Tudo fluindo bem!"
              description="Nenhum gargalo detectado. As escolas estão avançando sem travamentos."
              compact
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gargalosData.map((g, idx) => {
                const maxFaltam = gargalosData[0].faltam;
                const pct = Math.round((g.faltam / (setupEmAndamento || 1)) * 100);
                const barWidth = Math.round((g.faltam / maxFaltam) * 100);
                const severity = g.faltam >= 3 ? '#ef4444' : g.faltam === 2 ? '#f59e0b' : '#6366f1';
                return (
                  <div key={g.nome} title={`Escolas: ${g.escolas.join(', ')}`} style={{ cursor: 'default' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '.65rem', fontWeight: 800, color: 'white', background: severity, width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {idx + 1}
                        </span>
                        <span style={{ fontSize: '.82rem', fontWeight: 700, color: '#1e293b' }}>{g.nome}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '.78rem', fontWeight: 800, color: severity, background: `${severity}15`, padding: '1px 7px', borderRadius: 5 }}>
                          {g.faltam} escola{g.faltam !== 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: '.68rem', color: '#94a3b8' }}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{ height: 4, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${barWidth}%`, background: severity, borderRadius: 99, transition: 'width .5s ease' }} />
                    </div>
                    {g.escolas.length > 0 && (
                      <div style={{ fontSize: '.65rem', color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.escolas.slice(0, 3).join(' · ')}{g.escolas.length > 3 ? ` +${g.escolas.length - 3}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
